import { useState } from 'react'
import { CheckCircle, Loader2 } from 'lucide-react'
import FormField from './FormField'
import { useFormValidation } from '../hooks/useFormValidation'

export default function ContactForm({ className = '' }) {
  const [submitStatus, setSubmitStatus] = useState(null) // 'success', 'error', null

  const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    inquiryType: '',
    message: '',
    website: '' // Honeypot field - should remain empty
  }

  const validationRules = {
    firstName: {
      required: 'First name is required'
    },
    lastName: {
      required: 'Last name is required'
    },
    email: {
      required: 'Email is required',
      email: 'Please enter a valid email address'
    },
    phone: {
      required: 'Phone number is required',
      phone: 'Please enter a valid phone number'
    },
    inquiryType: {
      required: 'Please select how we can help you'
    },
    message: {
      required: 'Please tell us more about your requirements',
      minLength: {
        length: 10,
        message: 'Message must be at least 10 characters long'
      }
    },
    website: {
      required: false,
      validate: (value) => {
        if (value.trim() !== '') {
          return 'Invalid submission';
        }
        return true;
      }
    }
  }

  const inquiryOptions = [
    { value: 'hire', label: 'I need to hire drivers/staff' },
    { value: 'work', label: 'I\'m looking for work' },
    { value: 'emergency', label: 'Emergency staffing cover' },
    { value: 'general', label: 'General enquiry' },
    { value: 'partnership', label: 'Partnership opportunities' }
  ]

  const {
    values,
    errors,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    reset
  } = useFormValidation(initialValues, validationRules)

  const onSubmit = async (formData) => {
    try {
      const response = await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        
        if (response.status === 429) {
          throw new Error('Too many requests. Please wait 15 minutes before trying again.');
        } else if (response.status === 400) {
          throw new Error(errorData.error || 'Please check your form data and try again.');
        } else {
          throw new Error(errorData.error || 'Failed to send message. Please try again.');
        }
      }
      
      setSubmitStatus('success');
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
      
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    }
  };

  if (submitStatus === 'success') {
    return (
      <div className={`card p-8 text-center ${className}`}>
        <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-c9-blue mb-4">Message Sent Successfully!</h3>
        <p className="text-gray-600 mb-6">
          Thank you for contacting us. We'll get back to you within 24 hours.
        </p>
        <button
          onClick={() => {
            setSubmitStatus(null)
            reset()
          }}
          className="btn-primary"
        >
          Send Another Message
        </button>
      </div>
    )
  }

  return (
    <div className={`card p-8 ${className}`}>
      <h3 className="text-2xl font-bold text-c9-blue mb-6">Send Us a Message</h3>
      
      {submitStatus === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">
            There was an error sending your message. Please try again or call us directly.
          </p>
        </div>
      )}

      <form onSubmit={(e) => {
        e.preventDefault()
        handleSubmit(onSubmit)
      }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            label="First Name"
            name="firstName"
            placeholder="Enter your first name"
            value={values.firstName}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.firstName}
            required
          />
          
          <FormField
            label="Last Name"
            name="lastName"
            placeholder="Enter your last name"
            value={values.lastName}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.lastName}
            required
          />
        </div>

        <FormField
          label="Email Address"
          name="email"
          type="email"
          placeholder="Enter your email address"
          value={values.email}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.email}
          required
        />

        <FormField
          label="Phone Number"
          name="phone"
          type="tel"
          placeholder="Enter your phone number"
          value={values.phone}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.phone}
          required
        />

        <FormField
          label="How can we help?"
          name="inquiryType"
          type="select"
          placeholder="Select an option"
          value={values.inquiryType}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.inquiryType}
          options={inquiryOptions}
          required
        />

        <FormField
          label="Message"
          name="message"
          type="textarea"
          placeholder="Tell us more about your requirements..."
          value={values.message}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.message}
          rows={5}
          required
        />

        <FormField
          label="Website"
          name="website"
          type="text"
          placeholder="Enter your website"
          value={values.website}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.website}
          style={{ display: 'none' }}
        />

        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary w-full flex items-center justify-center"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              Sending Message...
            </>
          ) : (
            'Send Message'
          )}
        </button>
      </form>
    </div>
  )
}
